import React, { useState, useEffect } from "react";

const CommunityModal = ({ isOpen, onClose, communityData }) => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [toast, setToast] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSend = () => {
    const { name, email, phone, message } = formData;
    if (!name || !email || !phone || !message) {
      setToast({ type: "error", text: "Please fill in all fields." });
      return;
    }

    console.log(`Message to ${selectedMember.committee_name}:`, formData);
    setToast({
      type: "success",
      text: `Message sent to ${selectedMember.committee_name}!`,
    });
    setFormData({ name: "", email: "", phone: "", message: "" });
    setSelectedMember(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Get position badge color
  const getPositionColor = (position) => {
    const pos = position?.toLowerCase() || "";
    if (pos.includes("chair") && !pos.includes("vice")) return "#2C3E50"; // Dark Navy for Chair
    if (pos.includes("vice")) return "#00CC99"; // Teal for Vice Chair
    if (pos.includes("treasurer")) return "#009977"; // Darker Teal for Treasurer
    if (pos.includes("secretary")) return "#34495e"; // Blue Gray for Secretary
    return "#5f748a"; // Light Navy for Members
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerContainerStyle}>
          <div style={headerIconStyle}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21"
                stroke="#00CC99"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z"
                stroke="#00CC99"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13"
                stroke="#00CC99"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88"
                stroke="#00CC99"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div>
            <h2 style={headerTitleStyle}>Project Committee</h2>
            <p style={headerSubtitleStyle}>
              {communityData?.length || 0} members managing this project
            </p>
          </div>
          <button style={closeStyle} onClick={onClose} aria-label="Close modal">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Members Grid */}
        <div style={contentStyle}>
          {!communityData || communityData.length === 0 ? (
            <div style={emptyStateStyle}>
              <div style={emptyIconStyle}>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21"
                    stroke="#9ca3af"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z"
                    stroke="#9ca3af"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p style={emptyTextStyle}>No committee members available</p>
              <p style={emptySubTextStyle}>
                Committee information will appear here once added
              </p>
            </div>
          ) : (
            <div style={gridStyle}>
              {communityData.map((member, index) => (
                <div
                  key={index}
                  style={{
                    ...memberCard,
                    transform:
                      hoveredIndex === index
                        ? "translateY(-4px)"
                        : "translateY(0)",
                    boxShadow:
                      hoveredIndex === index
                        ? "0 12px 24px rgba(0, 0, 0, 0.15)"
                        : "0 2px 8px rgba(0, 0, 0, 0.08)",
                  }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={() => setSelectedMember(member)}
                >
                  {/* Position Badge */}
                  <div
                    style={{
                      ...positionBadgeStyle,
                      backgroundColor: getPositionColor(member.position_held),
                    }}
                  >
                    {member.position_held || "Member"}
                  </div>

                  {/* Avatar */}
                  <div style={avatarContainerStyle}>
                    <img
                      src={
                        member.committee_photo_url ||
                        `https://ui-avatars.com/api/?name=${encodeURIComponent(member.committee_name || "User")}&background=e6f9f5&color=00CC99&bold=true&size=120`
                      }
                      alt={member.committee_name || "Committee Member"}
                      style={avatarStyle}
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.committee_name || "User")}&background=e6f9f5&color=00CC99&bold=true&size=120`;
                      }}
                    />
                  </div>

                  {/* Member Info */}
                  <div style={memberInfoStyle}>
                    <h4 style={nameStyle}>
                      {member.committee_name || "Unknown"}
                    </h4>
                    {member.committee_mobile_number && (
                      <div style={contactRowStyle}>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7294C21.7209 20.9845 21.5573 21.2136 21.3521 21.4019C21.1468 21.5901 20.9046 21.7335 20.6407 21.8227C20.3769 21.9119 20.0974 21.9451 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77382 17.3147 6.72533 15.2662 5.18999 12.85C3.49997 10.2412 2.44824 7.27099 2.11999 4.18C2.09501 3.90347 2.12787 3.62476 2.21649 3.36162C2.30512 3.09849 2.44756 2.85669 2.63476 2.65162C2.82196 2.44655 3.0498 2.28271 3.30379 2.17052C3.55777 2.05833 3.83233 2.00026 4.10999 2H7.10999C7.5953 1.99522 8.06579 2.16708 8.43376 2.48353C8.80173 2.79999 9.04207 3.23945 9.10999 3.72C9.23662 4.68007 9.47144 5.62273 9.80999 6.53C9.94454 6.88792 9.97366 7.27691 9.8939 7.65088C9.81415 8.02485 9.62886 8.36811 9.35999 8.64L8.08999 9.91C9.51355 12.4135 11.5865 14.4864 14.09 15.91L15.36 14.64C15.6319 14.3711 15.9751 14.1858 16.3491 14.1061C16.7231 14.0263 17.1121 14.0555 17.47 14.19C18.3773 14.5286 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 22 16.92Z"
                            stroke="#6b7280"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span style={contactTextStyle}>
                          {member.committee_mobile_number}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Contact Button */}
                  <button style={contactBtnStyle}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M22 6L12 13L2 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Contact
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contact Form Modal */}
        {selectedMember && (
          <div style={popupOverlay} onClick={() => setSelectedMember(null)}>
            <div style={popupBox} onClick={(e) => e.stopPropagation()}>
              <button
                style={popupCloseStyle}
                onClick={() => setSelectedMember(null)}
                aria-label="Close form"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <div style={popupHeaderStyle}>
                <img
                  src={
                    selectedMember.committee_photo_url ||
                    `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedMember.committee_name || "User")}&background=e6f9f5&color=00CC99&bold=true&size=80`
                  }
                  alt={selectedMember.committee_name}
                  style={popupAvatarStyle}
                />
                <h3 style={popupTitle}>
                  Contact {selectedMember.committee_name}
                </h3>
                <span
                  style={{
                    ...positionBadgeStyle,
                    backgroundColor: getPositionColor(
                      selectedMember.position_held,
                    ),
                    position: "static",
                    marginTop: "8px",
                  }}
                >
                  {selectedMember.position_held || "Member"}
                </span>
              </div>

              <div style={formStyle}>
                <div style={inputGroupStyle}>
                  <label style={labelStyle}>Your Name</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleChange}
                    style={inputStyle}
                  />
                </div>
                <div style={inputGroupStyle}>
                  <label style={labelStyle}>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    style={inputStyle}
                  />
                </div>
                <div style={inputGroupStyle}>
                  <label style={labelStyle}>Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={handleChange}
                    style={inputStyle}
                  />
                </div>
                <div style={inputGroupStyle}>
                  <label style={labelStyle}>Message</label>
                  <textarea
                    name="message"
                    placeholder="Write your message here..."
                    value={formData.message}
                    onChange={handleChange}
                    style={textareaStyle}
                  />
                </div>

                <div style={popupBtns}>
                  <button
                    style={cancelBtn}
                    onClick={() => setSelectedMember(null)}
                  >
                    Cancel
                  </button>
                  <button style={sendBtn} onClick={handleSend}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M22 2L11 13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M22 2L15 22L11 13L2 9L22 2Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Send Message
                  </button>
                </div>

                {toast && (
                  <div
                    style={{
                      ...toastStyle,
                      backgroundColor:
                        toast.type === "success" ? "#ecfdf5" : "#fef2f2",
                      color: toast.type === "success" ? "#065f46" : "#991b1b",
                      borderLeft:
                        toast.type === "success"
                          ? "4px solid #10b981"
                          : "4px solid #ef4444",
                    }}
                  >
                    {toast.type === "success" ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ marginRight: "8px", flexShrink: 0 }}
                      >
                        <path
                          d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86"
                          stroke="#10b981"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M22 4L12 14.01L9 11.01"
                          stroke="#10b981"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ marginRight: "8px", flexShrink: 0 }}
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="#ef4444"
                          strokeWidth="2"
                        />
                        <path
                          d="M15 9L9 15M9 9L15 15"
                          stroke="#ef4444"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                    {toast.text}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Styles
const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  backdropFilter: "blur(4px)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
  padding: "20px",
  boxSizing: "border-box",
};

const modalStyle = {
  backgroundColor: "#fff",
  borderRadius: "20px",
  width: "100%",
  maxWidth: "720px",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  position: "relative",
  overflow: "hidden",
};

const headerContainerStyle = {
  background: "#fff",
  padding: "24px 28px",
  display: "flex",
  alignItems: "center",
  gap: "16px",
  position: "relative",
  borderBottom: "1px solid #e5e7eb",
};

const headerIconStyle = {
  width: "48px",
  height: "48px",
  backgroundColor: "#e6f9f5",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const headerTitleStyle = {
  fontSize: "22px",
  fontWeight: 700,
  color: "#1f2937",
  margin: 0,
  letterSpacing: "-0.02em",
};

const headerSubtitleStyle = {
  fontSize: "14px",
  color: "#6b7280",
  margin: "4px 0 0 0",
};

const closeStyle = {
  position: "absolute",
  top: "50%",
  right: "20px",
  transform: "translateY(-50%)",
  width: "36px",
  height: "36px",
  borderRadius: "10px",
  border: "none",
  background: "#f3f4f6",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#6b7280",
  transition: "all 0.2s ease",
};

const contentStyle = {
  padding: "24px",
  overflowY: "auto",
  flex: 1,
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
  gap: "20px",
};

const memberCard = {
  backgroundColor: "#fff",
  borderRadius: "16px",
  padding: "20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
  border: "1px solid #e5e7eb",
  position: "relative",
};

const positionBadgeStyle = {
  position: "absolute",
  top: "12px",
  right: "12px",
  fontSize: "11px",
  fontWeight: 600,
  color: "#fff",
  padding: "4px 10px",
  borderRadius: "20px",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const avatarContainerStyle = {
  width: "80px",
  height: "80px",
  borderRadius: "50%",
  padding: "3px",
  background: "linear-gradient(135deg, #00CC99 0%, #009977 100%)",
  marginBottom: "16px",
  marginTop: "8px",
};

const avatarStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  objectFit: "cover",
  border: "3px solid #fff",
};

const memberInfoStyle = {
  textAlign: "center",
  width: "100%",
};

const nameStyle = {
  fontWeight: 600,
  fontSize: "16px",
  color: "#1f2937",
  margin: "0 0 8px 0",
  lineHeight: 1.3,
};

const contactRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  marginBottom: "4px",
};

const contactTextStyle = {
  fontSize: "13px",
  color: "#6b7280",
};

const contactBtnStyle = {
  marginTop: "16px",
  padding: "10px 20px",
  backgroundColor: "#e6f9f5",
  border: "none",
  borderRadius: "10px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#00CC99",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  transition: "all 0.2s ease",
  width: "100%",
  justifyContent: "center",
};

const emptyStateStyle = {
  textAlign: "center",
  padding: "60px 20px",
};

const emptyIconStyle = {
  width: "80px",
  height: "80px",
  backgroundColor: "#f3f4f6",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 20px",
};

const emptyTextStyle = {
  fontSize: "18px",
  fontWeight: 600,
  color: "#374151",
  margin: "0 0 8px 0",
};

const emptySubTextStyle = {
  fontSize: "14px",
  color: "#9ca3af",
  margin: 0,
};

const popupOverlay = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  backdropFilter: "blur(4px)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 99999,
  padding: "20px",
  boxSizing: "border-box",
};

const popupBox = {
  backgroundColor: "#fff",
  borderRadius: "20px",
  width: "100%",
  maxWidth: "440px",
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  position: "relative",
};

const popupCloseStyle = {
  position: "absolute",
  top: "16px",
  right: "16px",
  width: "32px",
  height: "32px",
  borderRadius: "8px",
  border: "none",
  background: "#f3f4f6",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#6b7280",
  zIndex: 1,
  transition: "all 0.2s ease",
};

const popupHeaderStyle = {
  background: "linear-gradient(135deg, #f0fdf9 0%, #d1fae5 100%)",
  padding: "32px 24px",
  textAlign: "center",
  borderBottom: "1px solid #e5e7eb",
};

const popupAvatarStyle = {
  width: "72px",
  height: "72px",
  borderRadius: "50%",
  objectFit: "cover",
  border: "4px solid #fff",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
};

const popupTitle = {
  fontSize: "20px",
  fontWeight: 700,
  color: "#1f2937",
  margin: "16px 0 0 0",
};

const formStyle = {
  padding: "24px",
};

const inputGroupStyle = {
  marginBottom: "16px",
};

const labelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: 600,
  color: "#374151",
  marginBottom: "6px",
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: "10px",
  border: "1px solid #d1d5db",
  fontSize: "14px",
  color: "#1f2937",
  outline: "none",
  transition: "all 0.2s ease",
  boxSizing: "border-box",
};

const textareaStyle = {
  width: "100%",
  height: "100px",
  padding: "12px 14px",
  fontSize: "14px",
  borderRadius: "10px",
  border: "1px solid #d1d5db",
  color: "#1f2937",
  resize: "none",
  outline: "none",
  transition: "all 0.2s ease",
  boxSizing: "border-box",
};

const popupBtns = {
  display: "flex",
  gap: "12px",
  marginTop: "8px",
};

const sendBtn = {
  flex: 1,
  backgroundColor: "#00CC99",
  color: "#fff",
  padding: "12px 20px",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  transition: "all 0.2s ease",
};

const cancelBtn = {
  flex: 1,
  backgroundColor: "#f3f4f6",
  color: "#4b5563",
  padding: "12px 20px",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: 600,
  transition: "all 0.2s ease",
};

const toastStyle = {
  marginTop: "20px",
  padding: "14px 16px",
  borderRadius: "10px",
  fontSize: "14px",
  display: "flex",
  alignItems: "center",
  transition: "all 0.3s ease",
};

export default CommunityModal;
